import type { ApolloError } from "@apollo/client"
import cn from "@/v2-ui/utils/utils.cn"
import Icon from "@/v2-ui/icon"
import Skeleton from "@/v2-ui/skeleton"
import NotificationFeedItem, {
  type StaffNotificationRow,
} from "@/v2-backoffice-client/notification/feed/NotificationFeedItem"

export type NotificationFeedProps = {
  className?: string
  /** Rows to render — the dock owns the query and the tab/unread filters. */
  notifications: StaffNotificationRow[]
  isLoading?: boolean
  error?: ApolloError
  /** Copy for the empty state, phrased for the active filter. */
  emptyLabel?: string
}

function NotificationFeed(props: NotificationFeedProps) {
  const {
    className,
    notifications,
    isLoading = false,
    error,
    emptyLabel = "Events like churn alerts and invitations will show up here.",
  } = props

  // Full error state only when there's nothing to show — partial rows
  // render below with a non-blocking notice instead.
  if (error && notifications.length === 0 && !isLoading) {
    return (
      <div className={cn("notification-feed", className)}>
        <p className="px-4 py-6 text-sm text-destructive">
          Failed to load notifications: {error.message}
        </p>
      </div>
    )
  }

  if (isLoading && notifications.length === 0) {
    return (
      <div className={cn("notification-feed", className)} aria-busy>
        {Array.from({ length: 3 }, (_, index) => (
          <div key={index} className="flex gap-3 px-4 py-3">
            <Skeleton className="size-8 shrink-0 rounded-full" />
            <div className="flex-1 space-y-2 py-0.5">
              <Skeleton className="h-3 w-2/3" />
              <Skeleton className="h-3 w-2/5" />
            </div>
          </div>
        ))}
      </div>
    )
  }

  if (notifications.length === 0) {
    return (
      <div className={cn("notification-feed", className)}>
        <div className="flex flex-col items-center gap-3 px-6 py-16 text-center">
          <span className="flex size-12 items-center justify-center rounded-full bg-muted">
            <Icon src="bell" size={20} className="text-muted-foreground" />
          </span>
          <div className="space-y-1">
            <p className="text-sm font-medium">All caught up</p>
            <p className="text-xs text-muted-foreground">{emptyLabel}</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={cn("notification-feed", className)}>
      {error ? (
        <p className="border-b border-border px-4 py-2 text-xs text-destructive">
          Some notifications could not be refreshed: {error.message}
        </p>
      ) : null}
      <ul className="divide-y divide-border">
        {notifications.map((notification) => (
          <NotificationFeedItem
            key={notification.Id}
            notification={notification}
          />
        ))}
      </ul>
    </div>
  )
}

export default NotificationFeed
